import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { GameErrorOverlay } from '@/components/GameErrorOverlay';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import WebView, { WebViewNavigation } from '@/components/Webview';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { RootStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { AppOrientation } from '@/utils/app-orientation';
import { isWeb } from '@/utils/constants-platform-specific';
import { openExternalUrlAndroid } from '@/utils/webview';
import { useQueryClient } from '@tanstack/react-query';

import { useCasinoGameRecovery } from '../hooks/use-casino-game-recovery';
import { useCasinoGameWebView } from '../hooks/use-casino-game-web-view';

type ScreenProps = NativeStackScreenProps<RootStackParamList, 'CasinoGameWebView'>;

const styles = StyleSheet.create({
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    webView: {
        flex: 1,
    },
    loadingOverlay: {
        ...StyleSheet.absoluteFillObject,
        justifyContent: 'center',
        alignItems: 'center',
    },
    navBarTitle: {
        paddingLeft: 12,
    },
});

/**
 * Screen for displaying a casino game in a WebView.
 * Accepts either `gameId` (fetched fresh) or pre-resolved `gameParams` from the navigator.
 * Loading and error states are derived from `useCasinoGameWebView`.
 */
export const CasinoGameWebViewScreen = ({ route: { params } }: ScreenProps) => {
    const { t } = useTranslation('casino');
    const queryClient = useQueryClient();
    const webViewRef = useRef<WebView>(null);
    const [webViewLoading, setWebViewLoading] = useState(true);
    const { title, formattedUrl, isLoading, failsToLoad } = useCasinoGameWebView(params);
    const { webViewKey, showErrorOverlay, onProcessTerminated, retry, exitGame } = useCasinoGameRecovery(params.gameId);

    useEffect(() => {
        AppOrientation.allowAll();
        const webView = webViewRef.current;

        return () => {
            if (!isWeb) {
                webView?.stopLoading();
            }
            AppOrientation.lockToAllowed();
            // Invalidate recent games to refresh history on return to lobby
            queryClient.invalidateQueries({ queryKey: ['casino-game-history'] });
            queryClient.invalidateQueries({ queryKey: ['recently-played-games'] });
            queryClient.invalidateQueries({ queryKey: ['casino-free-spin-games'] });
        };
    }, [queryClient]);
    return (
        // own provider, so the insets update when the cover rotates
        <SafeAreaProvider style={common.flex}>
            <Screen edges={['top', 'bottom']}>
                <ScreenNavBar
                    title={title || 'Casino Game'}
                    closeIconMode="close"
                    titleOverrideStyle={styles.navBarTitle}
                />
                {failsToLoad ? (
                    <Box style={styles.loadingContainer}>
                        <Text textAlign="center">{t('couldntLoadGame')}</Text>
                    </Box>
                ) : isLoading || !formattedUrl ? (
                    <Box style={[styles.loadingContainer, { backgroundColor: ds.colorValue.background.neutral.base }]}>
                        <ActivityIndicator size="large" color={ds.colorValue.icon.primary} />
                    </Box>
                ) : (
                    <Box style={styles.webView}>
                        <WebView
                            key={webViewKey}
                            ref={webViewRef}
                            source={{ uri: formattedUrl }}
                            originWhitelist={['*']}
                            style={styles.webView}
                            mediaPlaybackRequiresUserAction={false}
                            allowsInlineMediaPlayback={true}
                            onLoadStart={() => setWebViewLoading(true)}
                            onLoadEnd={() => setWebViewLoading(false)}
                            onNavigationStateChange={(event: WebViewNavigation) => openExternalUrlAndroid(event?.url)}
                            onRenderProcessGone={({ nativeEvent }) => onProcessTerminated(nativeEvent.didCrash)}
                            onContentProcessDidTerminate={() => onProcessTerminated()}
                        />
                        {webViewLoading ? (
                            <Box
                                style={[
                                    styles.loadingOverlay,
                                    { backgroundColor: ds.colorValue.background.neutral.base },
                                ]}
                            >
                                {isWeb ? (
                                    <LoadingSpinner />
                                ) : (
                                    <ActivityIndicator size="large" color={ds.colorValue.icon.primary} />
                                )}
                            </Box>
                        ) : null}
                        {showErrorOverlay ? (
                            <GameErrorOverlay onReload={retry} onGoToLobby={exitGame} insetBottom={false} />
                        ) : null}
                    </Box>
                )}
            </Screen>
        </SafeAreaProvider>
    );
};
